import z from 'zod/v4';
import { meteringPointType } from '~/schemas/metering-points';
import { TAG_PREFIX } from '~/utils/tags';

export const ALLOCATION_STATUSES = z.enum([
  'locked',
  'provisional',
  'pending_registry_action',
  'transfer_request_submitted',
  'cancellation_request_submitted',
  'final',
]);

export enum ALLOCATION_MODE {
  AS_PRODUCED = 'AS_PRODUCED',
  AS_CONSUMED = 'AS_CONSUMED',
}

// The fixed attributes an interactive allocation groups demand by. {@link demandGroupingLevelSchema} and
// {@link supplyGroupingLevelSchema} add the organisation's own tags on top of each side's fixed attributes.
// The values are the volume attributes the `as_consumed` rule groups by, so they have to stay in step with it.
export const demandAttributeGroupingLevelEnum = z.enum([
  'certificate_label',
  'certificate_type',
  'contract_type',
  'counterparty_country',
  'counterparty_reference',
  'country',
  'deal_reference',
  'deal_tranche_reference',
  'duration',
  'max_delivery_date',
  'minimum_commercial_operation_date',
  'sub_national_region',
  'subsidy_regime',
  'supra_national_region',
  'technology',
]);
export type DemandAttributeGroupingLevel = z.infer<typeof demandAttributeGroupingLevelEnum>;

/**
 * A grouping level naming one of the organisation's tags, on either side: `tag_<name>`, for example
 * `tag_Product`.
 */
export const tagGroupingLevelSchema = z.templateLiteral([TAG_PREFIX, z.string().min(1)]);
export type TagGroupingLevel = z.infer<typeof tagGroupingLevelSchema>;

/** Every attribute demand can be grouped by: the fixed ones, plus one per demand tag. */
export const demandGroupingLevelSchema = z.union([demandAttributeGroupingLevelEnum, tagGroupingLevelSchema]);
export type DemandGroupingLevel = z.infer<typeof demandGroupingLevelSchema>;

export const supplyAttributeGroupingLevelEnum = z.enum([
  'certificate_label',
  'certificate_type',
  'contract_type',
  'counterparty_reference',
  'country',
  'deal_reference',
  'deal_tranche_reference',
  'duration',
  'inventory_certificate_expiration_date',
  'max_delivery_date',
  'minimum_commercial_operation_date',
  'production_device_reference',
  'registry',
  'registry_account_holder_identifier',
  'registry_account_identifier',
  'sub_national_region',
  'subsidy_regime',
  'supra_national_region',
  'technology',
  'type',
]);
export type SupplyAttributeGroupingLevel = z.infer<typeof supplyAttributeGroupingLevelEnum>;

/** Every attribute supply can be grouped by: the fixed ones, plus one per supply tag. */
export const supplyGroupingLevelSchema = z.union([supplyAttributeGroupingLevelEnum, tagGroupingLevelSchema]);
export type SupplyGroupingLevel = z.infer<typeof supplyGroupingLevelSchema>;

/** The allocation rules an allocation run can be asked to apply. Only the two the setup step offers. */
export const allocationRuleIdentifierEnum = z.enum(['as_consumed', 'as_produced']);

/**
 * The resolutions an allocation rule config accepts, finest to coarsest.
 *
 * Close to {@link durationEnum} but not the same vocabulary — quarters are `P1Q` rather than `P3M`, and
 * the open-ended member is `WHOLE_PERIOD` rather than `ALL`.
 */
export const allocationResolutionEnum = z.enum([
  'PT5M',
  'PT15M',
  'PT30M',
  'PT1H',
  'P1D',
  'P1M',
  'P1Q',
  'P1Y',
  'WHOLE_PERIOD',
]);
export type AllocationResolution = z.infer<typeof allocationResolutionEnum>;

export enum ALLOCATION_RESULT_TYPE {
  CURRENT = 'current',
  DRAFT = 'draft',
}

export enum ENERGY_UNIT {
  kwh = 'kWh',
  mwh = 'MWh',
  gwh = 'GWh',
  twh = 'TWh',
}

export const DEFAULT_ENERGY_UNIT = ENERGY_UNIT.mwh;
export const energyUnitEnum = z.enum(ENERGY_UNIT);

export const carbonContentUnitEnum = z.enum(['g', 'kg', 't', 'kt', 'Mt', 'Gt']);
export type CarbonUnit = z.infer<typeof carbonContentUnitEnum>;

export const CREATE_ALLOCATION_MODE = z.enum(['AS_PRODUCED_SET_VALUES', 'AS_CONSUMED_MAX_FILL']);
export type CreateAllocationModeType = z.infer<typeof CREATE_ALLOCATION_MODE>;

export const timeAttributeEnum = z.enum(['MINUTE', 'HOUR', 'DAY_OF_WEEK', 'MONTH', 'QUARTER', 'YEAR']);
export type TimeAttribute = z.infer<typeof timeAttributeEnum>;

export const matchingResolutionEnum = z.enum([
  'five_minute',
  'fifteen_minute',
  'thirty_minute',
  'hourly',
  'daily',
  'monthly',
  'quarterly',
  'yearly',
  'yearly_from_april',
  'yearly_from_may',
  'whole_period',
]);
export type MatchingResolution = z.infer<typeof matchingResolutionEnum>;

export const carbonContentErrorEnum = z.enum([
  // Shared errors
  'UNKNOWN_ERROR',
  'MATCHING_RESOLUTION_TOO_THIN',
  'NO_METERED_DEMAND_FOR_SELECTED_FILTERS',
  // location-based specific errors
  'INCOMPLETE_GRID_OUTPUT_DATA',
  'INCOMPLETE_CARBON_EMISSION_FACTOR_DATA',
  // market-based specific errors
  'INCOMPLETE_GRID_RESIDUAL_OUTPUT_DATA',
  'INCOMPLETE_RESIDUAL_CARBON_EMISSION_FACTOR_DATA',
  'MULTIPLE_GRIDS_NOT_SUPPORTED_FOR_CARBON_METRICS',
  'INVALID_ALLOCATED_TECHNOLOGIES_FOR_CARBON_METRICS',
  'NO_ALLOCATIONS_FOR_SELECTED_DEMAND',
  'CARBON_METRICS_NOT_SUPPORTED_FOR_GAS',
]);
export type CarbonContentError = z.infer<typeof carbonContentErrorEnum>;
export const CARBON_NOT_APPLICABLE_ERRORS: Array<CarbonContentError> = [
  carbonContentErrorEnum.enum.NO_METERED_DEMAND_FOR_SELECTED_FILTERS,
  carbonContentErrorEnum.enum.NO_ALLOCATIONS_FOR_SELECTED_DEMAND,
  carbonContentErrorEnum.enum.MULTIPLE_GRIDS_NOT_SUPPORTED_FOR_CARBON_METRICS,
  carbonContentErrorEnum.enum.CARBON_METRICS_NOT_SUPPORTED_FOR_GAS,
];

export const dateFormatEnum = z.enum([
  'HH:mm d MMM',
  'HH:mm',
  'MMM y',
  'MMM yy',
  'MMM',
  'P',
  'Pp',
  'QQQ',
  'QQQ yy',
  'ccc',
  'd MMM yy',
  'd MMM yyyy',
  'd MMM',
  'd MMM, HH:mm',
  'EEE d MMM',
  'EEE d MMM, HH:mm',
  'y',
  'yyyy-MM-dd',
  'yyyy-MM',
]);
export type DateFormat = z.infer<typeof dateFormatEnum>;

export const durationValues = ['ALL', 'PT5M', 'PT15M', 'PT30M', 'PT1H', 'P1D', 'P1M', 'P3M', 'P1Y'] as const;
export const durationEnum = z.enum(durationValues);
export type Duration = z.infer<typeof durationEnum>;

export const PPA_TRIGGER_TYPES = z.enum(['AUTO_PAY_AS_PRODUCED', 'AUTO_PAY_AS_CONSUMED', 'MANUAL']);

export const countryEnum = z.enum([
  'AF',
  'AL',
  'DZ',
  'AS',
  'AD',
  'AO',
  'AI',
  'AQ',
  'AG',
  'AR',
  'AM',
  'AW',
  'AU',
  'AT',
  'AZ',
  'BS',
  'BH',
  'BD',
  'BB',
  'BY',
  'BE',
  'BZ',
  'BJ',
  'BM',
  'BT',
  'BO',
  'BA',
  'BW',
  'BV',
  'BR',
  'IO',
  'BN',
  'BG',
  'BF',
  'BI',
  'KH',
  'CM',
  'CA',
  'CV',
  'KY',
  'CF',
  'TD',
  'CL',
  'CN',
  'CX',
  'CC',
  'CO',
  'KM',
  'CG',
  'CD',
  'CK',
  'CR',
  'CI',
  'HR',
  'CU',
  'CY',
  'CZ',
  'DK',
  'DJ',
  'DM',
  'DO',
  'EC',
  'EG',
  'SV',
  'GQ',
  'ER',
  'EE',
  'ET',
  'FK',
  'FO',
  'FJ',
  'FI',
  'FR',
  'GF',
  'PF',
  'TF',
  'GA',
  'GM',
  'GE',
  'DE',
  'GH',
  'GI',
  'GR',
  'GL',
  'GD',
  'GP',
  'GU',
  'GT',
  'GG',
  'GN',
  'GW',
  'GY',
  'HT',
  'HM',
  'VA',
  'HN',
  'HK',
  'HU',
  'IS',
  'IN',
  'ID',
  'IR',
  'IQ',
  'IE',
  'IM',
  'IL',
  'IT',
  'JM',
  'JP',
  'JE',
  'JO',
  'KZ',
  'KE',
  'KI',
  'KP',
  'KR',
  'KW',
  'KG',
  'LA',
  'LV',
  'LB',
  'LS',
  'LR',
  'LY',
  'LI',
  'LT',
  'LU',
  'MO',
  'MK',
  'MG',
  'MW',
  'MY',
  'MV',
  'ML',
  'MT',
  'MH',
  'MQ',
  'MR',
  'MU',
  'YT',
  'MX',
  'FM',
  'MD',
  'MC',
  'MN',
  'ME',
  'MS',
  'MA',
  'MZ',
  'MM',
  'NA',
  'NR',
  'NP',
  'NL',
  'AN',
  'NC',
  'NZ',
  'NI',
  'NE',
  'NG',
  'NU',
  'NF',
  'MP',
  'NO',
  'OM',
  'PK',
  'PW',
  'PS',
  'PA',
  'PG',
  'PY',
  'PE',
  'PH',
  'PN',
  'PL',
  'PT',
  'PR',
  'QA',
  'RE',
  'RO',
  'RU',
  'RW',
  'SH',
  'KN',
  'LC',
  'PM',
  'VC',
  'WS',
  'SM',
  'ST',
  'SA',
  'SN',
  'RS',
  'SC',
  'SL',
  'SG',
  'SK',
  'SI',
  'SB',
  'SO',
  'ZA',
  'GS',
  'SS',
  'ES',
  'LK',
  'SD',
  'SR',
  'SJ',
  'SZ',
  'SE',
  'CH',
  'SY',
  'TW',
  'TJ',
  'TZ',
  'TH',
  'TL',
  'TG',
  'TK',
  'TO',
  'TT',
  'TN',
  'TR',
  'TM',
  'TC',
  'TV',
  'UG',
  'UA',
  'AE',
  'GB',
  'US',
  'UM',
  'UY',
  'UZ',
  'VU',
  'VE',
  'VN',
  'VG',
  'VI',
  'WF',
  'EH',
  'YE',
  'ZM',
  'ZW',
  'Unknown',
  'Other',
]);

export const currencyEnum = z.enum([
  'ADP',
  'AED',
  'AFA',
  'AFN',
  'ALK',
  'ALL',
  'AMD',
  'ANG',
  'AOA',
  'AOK',
  'AON',
  'AOR',
  'ARA',
  'ARL',
  'ARM',
  'ARP',
  'ARS',
  'ATS',
  'AUD',
  'AWG',
  'AZM',
  'AZN',
  'BAD',
  'BAM',
  'BAN',
  'BBD',
  'BDT',
  'BEC',
  'BEF',
  'BEL',
  'BGL',
  'BGM',
  'BGN',
  'BGO',
  'BHD',
  'BIF',
  'BMD',
  'BND',
  'BOB',
  'BOL',
  'BOP',
  'BOV',
  'BRB',
  'BRC',
  'BRE',
  'BRL',
  'BRN',
  'BRR',
  'BRZ',
  'BSD',
  'BTN',
  'BUK',
  'BWP',
  'BYB',
  'BYN',
  'BYR',
  'BZD',
  'CAD',
  'CDF',
  'CHE',
  'CHF',
  'CHW',
  'CLE',
  'CLF',
  'CLP',
  'CNH',
  'CNX',
  'CNY',
  'COP',
  'COU',
  'CRC',
  'CSD',
  'CSK',
  'CUC',
  'CUP',
  'CVE',
  'CYP',
  'CZK',
  'DDM',
  'DEM',
  'DJF',
  'DKK',
  'DOP',
  'DZD',
  'ECS',
  'ECV',
  'EEK',
  'EGP',
  'ERN',
  'ESA',
  'ESB',
  'ESP',
  'ETB',
  'EUR',
  'FIM',
  'FJD',
  'FKP',
  'FRF',
  'GBP',
  'GEK',
  'GEL',
  'GHC',
  'GHS',
  'GIP',
  'GMD',
  'GNF',
  'GNS',
  'GQE',
  'GRD',
  'GTQ',
  'GWE',
  'GWP',
  'GYD',
  'HKD',
  'HNL',
  'HRD',
  'HRK',
  'HTG',
  'HUF',
  'IDR',
  'IEP',
  'ILP',
  'ILR',
  'ILS',
  'IMP',
  'INR',
  'IQD',
  'IRR',
  'ISJ',
  'ISK',
  'ITL',
  'JMD',
  'JOD',
  'JPY',
  'KES',
  'KGS',
  'KHR',
  'KMF',
  'KPW',
  'KRH',
  'KRO',
  'KRW',
  'KWD',
  'KYD',
  'KZT',
  'LAK',
  'LBP',
  'LKR',
  'LRD',
  'LSL',
  'LTL',
  'LTT',
  'LUC',
  'LUF',
  'LUL',
  'LVL',
  'LVR',
  'LYD',
  'MAD',
  'MAF',
  'MCF',
  'MDC',
  'MDL',
  'MGA',
  'MGF',
  'MKD',
  'MKN',
  'MLF',
  'MMK',
  'MNT',
  'MOP',
  'MRO',
  'MRU',
  'MTL',
  'MTP',
  'MUR',
  'MVP',
  'MVR',
  'MWK',
  'MXN',
  'MXP',
  'MXV',
  'MYR',
  'MZE',
  'MZM',
  'MZN',
  'NAD',
  'NGN',
  'NIC',
  'NIO',
  'NLG',
  'NOK',
  'NPR',
  'NZD',
  'OMR',
  'PAB',
  'PEI',
  'PEN',
  'PES',
  'PGK',
  'PHP',
  'PKR',
  'PLN',
  'PLZ',
  'PTE',
  'PYG',
  'QAR',
  'RHD',
  'ROL',
  'RON',
  'RSD',
  'RUB',
  'RUR',
  'RWF',
  'SAR',
  'SBD',
  'SCR',
  'SDD',
  'SDG',
  'SDP',
  'SEK',
  'SGD',
  'SHP',
  'SIT',
  'SKK',
  'SLE',
  'SLL',
  'SOS',
  'SRD',
  'SRG',
  'SSP',
  'STD',
  'STN',
  'SUR',
  'SVC',
  'SYP',
  'SZL',
  'THB',
  'TJR',
  'TJS',
  'TMM',
  'TMT',
  'TND',
  'TOP',
  'TPE',
  'TRL',
  'TRY',
  'TTD',
  'TVD',
  'TWD',
  'TZS',
  'UAH',
  'UAK',
  'UGS',
  'UGX',
  'USD',
  'USN',
  'USS',
  'UYI',
  'UYP',
  'UYU',
  'UYW',
  'UZS',
  'VEB',
  'VED',
  'VEF',
  'VES',
  'VND',
  'VNN',
  'VUV',
  'WST',
  'XAF',
  'XAG',
  'XAU',
  'XBA',
  'XBB',
  'XBC',
  'XBD',
  'XCD',
  'XDR',
  'XEU',
  'XFO',
  'XFU',
  'XOF',
  'XPD',
  'XPF',
  'XPT',
  'XRE',
  'XSU',
  'XTS',
  'XUA',
  'XXX',
  'YDD',
  'YER',
  'YUD',
  'YUM',
  'YUN',
  'YUR',
  'ZAL',
  'ZAR',
  'ZMK',
  'ZMW',
  'ZRN',
  'ZRZ',
  'ZWD',
  'ZWL',
  'ZWN',
  'ZWR',
]);

export const technologyEnum = z.enum([
  'AnyRenewable',
  'Battery',
  'Biodegradable',
  'Biogas',
  'Biomass',
  'AnaerobicDigestionGeneric',
  'AnaerobicDigestionMixed',
  'AnaerobicDigestionProduct',
  'AnaerobicDigestionResidue',
  'AnaerobicDigestionWaste',
  'Coal',
  'Gas',
  'GeoThermal',
  'Hydro',
  'HydrogenEngine',
  'LandfillGas',
  'Marine',
  'Nuclear',
  'OffshoreWind',
  'OnshoreWind',
  'Other',
  'SewageGas',
  'Solar',
  'Unknown',
  'Wind',
  'AnyFossil',
]);

// First three groups are expected group names
// The rest are just placeholders, as the algorithm cycles through the list, from position 4 to 10
export const technologyGroupEnum = z.enum([
  'Wind / Solar',
  'Wind / Solar / Hydro',
  'Wind / Solar / Hydro / Nuclear',
  'Grouping 4',
  'Grouping 5',
  'Grouping 6',
  'Grouping 7',
  'Grouping 8',
  'Grouping 9',
  'Grouping 10',
  'Unspecified',
]);

export const commodityTypeEnum = z.enum(['ELECTRICITY', 'GAS']);
export type CommodityType = z.infer<typeof commodityTypeEnum>;

export const subsidyRegimeEnum = z.enum(['SUBSIDISED', 'UNSUBSIDISED']);
export type SubsidyRegime = z.infer<typeof subsidyRegimeEnum>;

export const demandContractTypeEnum = z.enum(['METERED_DEMAND', 'SELL_DEAL']);

// The metering data coverage endpoints show one side of the meters at a time.
export const meterTypeEnum = meteringPointType.extract(['consumption', 'production']);
export type MeterType = z.infer<typeof meterTypeEnum>;

export const taggedEntityEnum = z.enum(['metering_point_state', 'asset_state', 'deal_tranche']);
